/**
 * Socket Context
 * ==============
 * Provides WebSocket connection management for real-time updates.
 *
 * Features:
 * - Auto-connect when user is authenticated
 * - Auto-reconnect on connection loss
 * - Room subscription management (documents, jobs)
 * - Event listener registration
 *
 * Usage:
 *   // In component:
 *   const { subscribe, on, off, isConnected } = useSocket();
 *
 *   useEffect(() => {
 *       // Subscribe to document updates
 *       subscribe('document', documentId);
 *
 *       // Listen for OCR status changes
 *       const handler = (data) => console.log('OCR update:', data);
 *       on('ocr_status', handler);
 *
 *       return () => {
 *           off('ocr_status', handler);
 *       };
 *   }, [documentId]);
 *
 * Events:
 *   - ocr_status: OCR processing status changed
 *   - match_progress: Bulk matching progress update
 *   - match_complete: Bulk matching completed
 *   - notification: General notification
 *   - document_updated: Document was modified
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { config } from '../utils/config';

// Event types for type safety
export interface OcrStatusEvent {
    document_id: string;
    status: 'processing' | 'completed' | 'failed';
    progress: number;
    data?: any;
    error?: string;
    timestamp: string;
}

export interface MatchProgressEvent {
    job_id: string;
    current: number;
    total: number;
    percent: number;
    status: 'processing' | 'completed' | 'failed';
    result?: {
        invoice_id: string;
        quote_id: string;
        match_score: number;
        status: string;
    };
    timestamp: string;
}

export interface MatchCompleteEvent {
    job_id: string;
    status: 'completed';
    total_matches: number;
    summary: {
        matched?: number;
        discrepancy?: number;
        unmatched?: number;
    };
    timestamp: string;
}

export interface NotificationEvent {
    type: 'info' | 'success' | 'warning' | 'error';
    message: string;
    data?: any;
    timestamp: string;
}

// Event handler types
type EventHandler<T = any> = (data: T) => void;

interface SocketContextType {
    // Connection state
    isConnected: boolean;
    connectionError: string | null;

    // Subscription management
    subscribe: (type: 'document' | 'job' | 'company', id: string) => void;
    unsubscribe: (type: 'document' | 'job' | 'company', id: string) => void;

    // Event listeners
    on: <T = any>(event: string, handler: EventHandler<T>) => void;
    off: <T = any>(event: string, handler: EventHandler<T>) => void;

    // Manual emit (for custom events)
    emit: (event: string, data: any) => void;
}

interface SocketProviderProps {
    children: ReactNode;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const useSocket = (): SocketContextType => {
    const context = useContext(SocketContext);
    if (context === undefined) {
        throw new Error('useSocket must be used within a SocketProvider');
    }
    return context;
};

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
    const { token, user, isAuthenticated } = useAuth();
    const [isConnected, setIsConnected] = useState(false);
    const [connectionError, setConnectionError] = useState<string | null>(null);

    // Socket reference (persists across renders)
    const socketRef = useRef<Socket | null>(null);

    // Track active subscriptions for reconnection
    const subscriptionsRef = useRef<Set<string>>(new Set());

    /**
     * Initialize socket connection when authenticated
     */
    useEffect(() => {
        if (!isAuthenticated || !token) {
            // Disconnect if not authenticated
            if (socketRef.current) {
                socketRef.current.disconnect();
                socketRef.current = null;
                setIsConnected(false);
            }
            return;
        }

        // Create socket connection
        // Socket.IO will connect to the same host as the Invoice API
        const socketUrl = config.INVOICE_API_URL;

        socketRef.current = io(socketUrl, {
            transports: ['websocket', 'polling'],
            autoConnect: true,
            reconnection: true,
            reconnectionAttempts: 10,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            auth: {
                token: token
            }
        });

        const socket = socketRef.current;

        // Connection event handlers
        socket.on('connect', () => {
            console.log('WebSocket connected:', socket.id);
            setIsConnected(true);
            setConnectionError(null);

            // Re-subscribe to all rooms after reconnection
            subscriptionsRef.current.forEach(subscription => {
                const [type, id] = subscription.split(':');
                socket.emit(`join_${type}`, { [`${type}_id`]: id });
            });

            // Auto-join company room if user has company_id
            if (user?.company_id) {
                socket.emit('join_company', { company_id: user.company_id });
            }
        });

        socket.on('disconnect', (reason) => {
            console.log('WebSocket disconnected:', reason);
            setIsConnected(false);
            if (reason === 'io server disconnect') {
                // Server initiated disconnect, try to reconnect
                socket.connect();
            }
        });

        socket.on('connect_error', (error) => {
            console.error('WebSocket connection error:', error.message);
            setConnectionError(error.message);
            setIsConnected(false);
        });

        // Cleanup on unmount
        return () => {
            socket.disconnect();
            socketRef.current = null;
            setIsConnected(false);
        };
    }, [isAuthenticated, token, user?.company_id]);

    /**
     * Subscribe to a room for updates
     */
    const subscribe = useCallback((type: 'document' | 'job' | 'company', id: string) => {
        if (!socketRef.current || !id) return;

        const subscription = `${type}:${id}`;
        if (subscriptionsRef.current.has(subscription)) return; // Already subscribed

        subscriptionsRef.current.add(subscription);
        socketRef.current.emit(`join_${type}`, { [`${type}_id`]: id });
        console.log(`Subscribed to ${type}:${id}`);
    }, []);

    /**
     * Unsubscribe from a room
     */
    const unsubscribe = useCallback((type: 'document' | 'job' | 'company', id: string) => {
        if (!socketRef.current || !id) return;

        const subscription = `${type}:${id}`;
        subscriptionsRef.current.delete(subscription);
        socketRef.current.emit(`leave_${type}`, { [`${type}_id`]: id });
        console.log(`Unsubscribed from ${type}:${id}`);
    }, []);

    /**
     * Register event listener
     */
    const on = useCallback(<T = any>(event: string, handler: EventHandler<T>) => {
        if (!socketRef.current) return;
        socketRef.current.on(event, handler);
    }, []);

    /**
     * Remove event listener
     */
    const off = useCallback(<T = any>(event: string, handler: EventHandler<T>) => {
        if (!socketRef.current) return;
        socketRef.current.off(event, handler);
    }, []);

    /**
     * Emit custom event
     */
    const emit = useCallback((event: string, data: any) => {
        if (!socketRef.current) return;
        socketRef.current.emit(event, data);
    }, []);

    const value: SocketContextType = {
        isConnected,
        connectionError,
        subscribe,
        unsubscribe,
        on,
        off,
        emit
    };

    return (
        <SocketContext.Provider value={value}>
            {children}
        </SocketContext.Provider>
    );
};

export default SocketContext;
